import { Autocomplete, TextField, SxProps, Theme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import PortalTooltip, { MUITooltipPlacement } from "./PortalTooltip";
import type { BaseDataModel } from "../../api/types";

interface Props<T extends BaseDataModel> {
	onChange: (value: T | null) => void | Promise<void>;
	loading?: boolean;
	options: T[];
	selectedOption: T | null;
	label: string;
	optionFormatter?: (option: T) => string;
	tooltip?: React.ReactNode;
	size?: "small" | "medium" | undefined;
	style?: React.CSSProperties;
	placement?: MUITooltipPlacement;
	id?: string;
	className?: string;
	optionLabelField?: keyof T;
	sx?: SxProps<Theme>;
}

export default function F<T extends BaseDataModel>({
	onChange,
	loading,
	options,
	selectedOption,
	label,
	optionFormatter,
	tooltip,
	size,
	style,
	placement,
	id,
	className,
	optionLabelField = "name" as keyof T,
	sx,
}: Props<T>) {
	return (
		<Grid size={12} sx={sx}>
			<Autocomplete
				id={id}
				className={className}
				style={style}
				disabled={!!loading}
				options={options}
				value={selectedOption}
				getOptionKey={(option) => option.id || JSON.stringify(option)}
				getOptionLabel={(option) =>
					optionFormatter ? optionFormatter(option) : (option[optionLabelField]?.toString() ?? "")
				}
				size={size ?? "medium"}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				onChange={async (event, value) => {
					event.stopPropagation();
					await onChange(value);
				}}
				renderInput={(params) => {
					return tooltip ? (
						<PortalTooltip title={tooltip} placement={placement}>
							<TextField {...params} label={label} variant="outlined" />
						</PortalTooltip>
					) : (
						<TextField {...params} label={label} variant="outlined" />
					);
				}}
			/>
		</Grid>
	);
}
